import { useEffect, useState } from "react";
import "../styles/Reports.css"; // Importando o CSS
import { fetchPatientsData } from "../services/api"; // Serviço para requisição de API
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [patientsData, setPatientsData] = useState([]);
  const [reportType, setReportType] = useState("");
  const [reportContent, setReportContent] = useState(null);

  // Buscar dados dos pacientes quando o componente monta
  useEffect(() => {
    fetchPatientPopulation();
  }, []);

  const fetchPatientPopulation = async () => {
    setLoading(true);
    const userId = localStorage.getItem("patientId");
    const authToken = localStorage.getItem("authToken");

    if (!userId || !authToken) {
      console.error("User ID ou Token não encontrado.");
      setLoading(false);
      return;
    }

    try {
      const data = await fetchPatientsData(userId, authToken);
      setPatientsData(data);
    } catch (error) {
      console.error("Erro ao buscar pacientes:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = (selectedType) => {
    setReportType(selectedType);
    let reportHtml = "";

    switch (selectedType) {
      case "contactReport":
        reportHtml = generateContactReport();
        break;
      case "locationDistribution":
        reportHtml = generateLocationDistributionReport();
        break;
      case "demographicData":
        reportHtml = generateDemographicDataReport();
        break;
      case "fullDetails":
        reportHtml = generateFullDetailsReport();
        break;
      case "activityReport":
        reportHtml = generateActivityReport();
        break;
      case "financialReport":
        reportHtml = generateFinancialReport();
        break;
      default:
        reportHtml = "<p>Selecione um tipo de relatório válido.</p>";
    }

    setReportContent(reportHtml);
  };

  const generateContactReport = () => {
    if (patientsData.length === 0) return <p>Nenhum dado disponível.</p>;

    const data = {
      labels: patientsData.map(patient => patient.Name),
      datasets: [
        {
          label: 'Contatos',
          data: patientsData.map(patient => patient.email ? 1 : 0),
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };

    return <Bar data={data} />;
  };

  const generateLocationDistributionReport = () => {
    if (patientsData.length === 0) return <p>Nenhum dado disponível.</p>;

    const locationCount = {};

    patientsData.forEach(patient => {
      const location = patient.location || 'Desconhecido';
      locationCount[location] = (locationCount[location] || 0) + 1;
    });

    const data = {
      labels: Object.keys(locationCount),
      datasets: [
        {
          label: 'Distribuição por Localidade',
          data: Object.values(locationCount),
          backgroundColor: [
            'rgba(255, 99, 132, 0.2)',
            'rgba(54, 162, 235, 0.2)',
            'rgba(255, 206, 86, 0.2)',
            'rgba(75, 192, 192, 0.2)',
            'rgba(153, 102, 255, 0.2)',
            'rgba(255, 159, 64, 0.2)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };

    return <Pie data={data} />;
  };
  
  const generateDemographicDataReport = () => {
    if (patientsData.length === 0) return <p>Nenhum dado disponível.</p>;

    const demographics = {
      gender: {},
      ageGroups: {},
    };
  
    patientsData.forEach(patient => {
      const gender = patient.gender || "Desconhecido";
      demographics.gender[gender] = (demographics.gender[gender] || 0) + 1;
  
      const age = calculateAge(patient.birthDate);
      const ageGroup = getAgeGroup(age);
      demographics.ageGroups[ageGroup] = (demographics.ageGroups[ageGroup] || 0) + 1;
    });
  
    const genderData = {
      labels: Object.keys(demographics.gender),
      datasets: [
        {
          label: 'Gênero',
          data: Object.values(demographics.gender),
          backgroundColor: [
            'rgba(255, 99, 132, 0.2)',
            'rgba(54, 162, 235, 0.2)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };

    const ageGroupData = {
      labels: Object.keys(demographics.ageGroups),
      datasets: [
        {
          label: 'Faixa Etária',
          data: Object.values(demographics.ageGroups),
          backgroundColor: [
            'rgba(75, 192, 192, 0.2)',
            'rgba(153, 102, 255, 0.2)',
            'rgba(255, 159, 64, 0.2)',
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };

    return (
      <div>
        <Pie data={genderData} />
        <Bar data={ageGroupData} />
      </div>
    );
  };
  
  const generateFullDetailsReport = () => {
    if (patientsData.length === 0) return <p>Nenhum dado disponível.</p>;

    const data = {
      labels: patientsData.map(patient => patient.Name),
      datasets: [
        {
          label: 'Idade',
          data: patientsData.map(patient => calculateAge(patient.birthDate)),
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1,
        },
      ],
    };

    return <Bar data={data} />;
  };
  
  const generateActivityReport = () => {
    if (patientsData.length === 0) return <p>Nenhum dado disponível.</p>;

    const data = {
      labels: patientsData.map(patient => patient.Name),
      datasets: [
        {
          label: 'Última Visita',
          data: patientsData.map(patient => new Date(patient.lastVisit).getTime()),
          backgroundColor: 'rgba(255, 206, 86, 0.2)',
          borderColor: 'rgba(255, 206, 86, 1)',
          borderWidth: 1,
        },
      ],
    };

    return <Bar data={data} />;
  };
  
  const generateFinancialReport = () => {
    if (patientsData.length === 0) return <p>Nenhum dado disponível.</p>;

    const data = {
      labels: patientsData.map(patient => patient.Name),
      datasets: [
        {
          label: 'Total Pago',
          data: patientsData.map(patient => patient.totalPaid || 0),
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };

    return <Bar data={data} />;
  };
  
  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const difference = Date.now() - birth.getTime();
    const ageDate = new Date(difference);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };
  
  const getAgeGroup = (age) => {
    if (age < 18) return "Menos de 18";
    if (age >= 18 && age < 35) return "18-34";
    if (age >= 35 && age < 55) return "35-54";
    return "55+";
  };
  

  return (
    <div id="content">
      {loading && (
        <div id="loading-relatorios">
          <div className="spinner"></div>
        </div>
      )}

      <div id="relatorio" className="section">
        <div className="header-container">
          <h1>Relatório de Usuários</h1>
          <select onChange={(e) => generateReport(e.target.value)} value={reportType}>
            <option value="">Selecione um Tipo de Relatório</option>
            <option value="contactReport">Relatório de Contato dos Pacientes</option>
            <option value="locationDistribution">Relatório de Distribuição por Localidade</option>
            <option value="demographicData">Relatório de Dados Demográficos</option>
            <option value="fullDetails">Relatório de Detalhes Completos dos Pacientes</option>
            <option value="activityReport">Relatório de Atividade dos Pacientes</option>
            <option value="financialReport">Relatório Financeiro de Pacientes</option>
          </select>
        </div>

        <div id="reportContainer">
          {reportContent}
        </div>
      </div>
    </div>
  );
};

export default Reports;
